import { useCallback, useState } from "react";

import { toast } from "@/lib/use-toast";

import { saveUrlToGallery } from "@/api/provider/scribe/notes";

/**
 * Hook to save an image from a URL to a patient's gallery.
 * Uses a backend endpoint to fetch the image server-side (avoids CORS issues
 * with external URLs like Meta CDN) and upload it as a gallery attachment.
 */
export function useSaveToGallery(patientId: string | null | undefined) {
  const [savingUrls, setSavingUrls] = useState<Set<string>>(new Set());

  const saveToGallery = useCallback(
    async (imageUrl: string) => {
      if (!patientId) {
        toast({
          title: "No patient linked",
          description:
            "Link a patient to this conversation to save images to their gallery.",
          variant: "error",
        });
        return;
      }

      setSavingUrls((prev) => new Set(prev).add(imageUrl));

      try {
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
        const name = `chat-image-${timestamp}`;

        await saveUrlToGallery(imageUrl, patientId, name);

        toast({
          title: "Saved",
          description: "Image saved to patient gallery",
          variant: "success",
        });
      } catch (error) {
        console.error("Failed to save image to gallery:", error);
        toast({
          title: "Save Failed",
          description: "Unable to save the image to the gallery.",
          variant: "error",
        });
      } finally {
        setSavingUrls((prev) => {
          const next = new Set(prev);
          next.delete(imageUrl);
          return next;
        });
      }
    },
    [patientId],
  );

  const isSaving = useCallback(
    (imageUrl: string) => savingUrls.has(imageUrl),
    [savingUrls],
  );

  return { saveToGallery, isSaving };
}
